import PropTypes from 'prop-types'
import { useEffect, useReducer, useCallback, useMemo } from 'react'
// utils
import axios from 'src/utils/axios'
//
import { useDispatch, useSelector } from 'react-redux'
import keycloak from 'src/utils/keycloak'
import { ajaxCall } from 'src/reducers/axios'
import { showMessage } from 'src/reducers/message'
import { paths } from 'src/routes/paths'
import { useNavigate } from 'react-router'
import { isValidToken, setSession } from './utils'
import { AuthContext } from './auth-context'

const initialState = {
  data: null,
  loading: true,
  reInit: false,
}

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      loading: false,
      data: action.payload,
    }
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      data: action.payload,
      reInit: true,
    }
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      data: action.payload,
    }
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      reInit: false,
      data: null,
    }
  }
  return state
}

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const { appConfig } = useSelector((store) => store.app)
  const { reInit } = useSelector((store) => store.axios)

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(window.appConfig.tokenStorageKey)

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken)

        const response = await axios.get(appConfig.actions.auth.appConfig.path)

        const { data } = response.data

        dispatch({
          type: 'INITIAL',
          payload: {
            ...data,
            accessToken,
          },
        })
      } else {
        dispatch({
          type: 'INITIAL',
          payload: null,
        })
      }
    } catch (error) {
      dispatch({
        type: 'INITIAL',
        payload: null,
      })
    }
  }, [appConfig.actions])

  useEffect(() => {
    initialize()
  }, [initialize, state.reInit, reInit])

  const { doLogout } = keycloak

  const outerDispatch = useDispatch()

  const navigate = useNavigate()

  // REGISTER
  const register = useCallback(
    async (data) => {
      outerDispatch(
        ajaxCall({
          config: {
            url: appConfig.actions.auth.register.path,
            method: 'post',
            data,
          },
          success: () => {
            navigate(paths.login)

            outerDispatch(
              showMessage({
                message: 'Utente creato',
                type: 'success',
                autoHide: true,
              })
            )
          },
          error: (err) => {
            outerDispatch(
              showMessage({
                message: err.message || 'Si è verificato un errore, riprova',
                type: 'error',
                autoHide: true,
              })
            )
          },
          activeLoading: true,
          authAction: true,
        })
      )
    },
    [appConfig.actions, outerDispatch, navigate]
  )

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null)

    dispatch({
      type: 'LOGOUT',
    })

    doLogout()
  }, [doLogout])

  // SAVE REDIRECT PATH
  const setRedirect = useCallback((returnTo) => {
    sessionStorage.setItem('login-redirect', returnTo)
  }, [])

  // ----------------------------------------------------------------------

  const memoizedValue = useMemo(
    () => ({
      data: state.data,
      loading: state.loading,
      //
      register,
      logout,
      setRedirect,
    }),
    [logout, register, setRedirect, state]
  )

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>
}

AuthProvider.propTypes = {
  children: PropTypes.node,
}
